import type {
  ArtPack,
  AuthoredDirectionalAnimTable,
  SpriteTemplate,
} from "../../art/types";
import {
  loadAuthoredDirectionalAnimSet,
  singleFrameAnimSet,
} from "../../art/anim-set";
import affiliation from "../tables/art/packs/portal-ffa/affiliation.json";
import packJson from "../tables/art/packs/portal-ffa/pack.json";
import palette from "../tables/art/packs/portal-ffa/palette.json";
import chaser from "../tables/art/packs/portal-ffa/sprites/chaser.json";
import guardian from "../tables/art/packs/portal-ffa/sprites/guardian.json";
import hero from "../tables/art/packs/portal-ffa/sprites/hero.json";
import mana from "../tables/art/packs/portal-ffa/sprites/mana.json";
import master from "../tables/art/packs/portal-ffa/sprites/master.json";
import raider from "../tables/art/packs/portal-ffa/sprites/raider.json";
import soldier from "../tables/art/packs/portal-ffa/sprites/soldier.json";
import weapon from "../tables/art/packs/portal-ffa/sprites/weapon.json";
import fxChaser from "../tables/art/packs/portal-ffa/sprites/fx_ffa_chaser_muzzle.json";
import fxGuardian from "../tables/art/packs/portal-ffa/sprites/fx_ffa_guardian_bash.json";
import fxHero from "../tables/art/packs/portal-ffa/sprites/fx_ffa_hero_cleave.json";
import fxMaster from "../tables/art/packs/portal-ffa/sprites/fx_ffa_master_slash.json";
import fxRaider from "../tables/art/packs/portal-ffa/sprites/fx_ffa_raider_arc.json";
import fxSoldier from "../tables/art/packs/portal-ffa/sprites/fx_ffa_soldier_thrust.json";
import fxWeapon from "../tables/art/packs/portal-ffa/sprites/fx_ffa_weapon_muzzle.json";
import chaserAnim from "../tables/art/packs/portal-ffa/anims/chaser.json";
import guardianAnim from "../tables/art/packs/portal-ffa/anims/guardian.json";
import heroAnim from "../tables/art/packs/portal-ffa/anims/hero.json";
import masterAnim from "../tables/art/packs/portal-ffa/anims/master.json";
import raiderAnim from "../tables/art/packs/portal-ffa/anims/raider.json";
import soldierAnim from "../tables/art/packs/portal-ffa/anims/soldier.json";
import fxChaserAnim from "../tables/art/packs/portal-ffa/anims/fx_ffa_chaser_muzzle.json";
import fxGuardianAnim from "../tables/art/packs/portal-ffa/anims/fx_ffa_guardian_bash.json";
import fxHeroAnim from "../tables/art/packs/portal-ffa/anims/fx_ffa_hero_cleave.json";
import fxMasterAnim from "../tables/art/packs/portal-ffa/anims/fx_ffa_master_slash.json";
import fxRaiderAnim from "../tables/art/packs/portal-ffa/anims/fx_ffa_raider_arc.json";
import fxSoldierAnim from "../tables/art/packs/portal-ffa/anims/fx_ffa_soldier_thrust.json";
import fxWeaponAnim from "../tables/art/packs/portal-ffa/anims/fx_ffa_weapon_muzzle.json";

/** Combat roles: Dropfront-seeded authored dirs. weapon/mana stay single-frame. */
const COMBAT_ANIMS: Record<string, AuthoredDirectionalAnimTable> = {
  master: masterAnim as AuthoredDirectionalAnimTable,
  guardian: guardianAnim as AuthoredDirectionalAnimTable,
  soldier: soldierAnim as AuthoredDirectionalAnimTable,
  raider: raiderAnim as AuthoredDirectionalAnimTable,
  chaser: chaserAnim as AuthoredDirectionalAnimTable,
  hero: heroAnim as AuthoredDirectionalAnimTable,
};

const FX_ANIMS: Record<string, AuthoredDirectionalAnimTable> = {
  fx_ffa_master_slash: fxMasterAnim as AuthoredDirectionalAnimTable,
  fx_ffa_hero_cleave: fxHeroAnim as AuthoredDirectionalAnimTable,
  fx_ffa_raider_arc: fxRaiderAnim as AuthoredDirectionalAnimTable,
  fx_ffa_guardian_bash: fxGuardianAnim as AuthoredDirectionalAnimTable,
  fx_ffa_soldier_thrust: fxSoldierAnim as AuthoredDirectionalAnimTable,
  fx_ffa_chaser_muzzle: fxChaserAnim as AuthoredDirectionalAnimTable,
  fx_ffa_weapon_muzzle: fxWeaponAnim as AuthoredDirectionalAnimTable,
};

const spriteList = [
  master,
  guardian,
  soldier,
  raider,
  chaser,
  hero,
  weapon,
  mana,
  fxMaster,
  fxHero,
  fxRaider,
  fxGuardian,
  fxSoldier,
  fxChaser,
  fxWeapon,
] as SpriteTemplate[];

/**
 * Combat: authored dirs e/n/s (+ W=flip(E)). Presentation plays facing ×
 * idle/walk/attack/death; UnitRig owns weapon (+ grip). Props: static side.
 *
 * Seeded DF kits can share silhouettes (master/raider/hero←assault). Stamp a
 * tiny role tag so pack validation hamming stays ≥ min until unique art lands.
 */
function stampRoleSilhouetteTag(rows: string[], roleId: string): string[] {
  const out = rows.map((r) => [...r]);
  const n = [...roleId].reduce((a, c) => a + c.charCodeAt(0), 0);
  // Hamming uses opaque vs empty — must add/remove solids, not recolor.
  const bumpX = 1 + (n % 10);
  const bumpY = 1 + ((n * 3) % 10);
  const holeX = 50 + (n % 8);
  const holeY = 8 + ((n * 5) % 10);
  for (let dy = 0; dy < 5; dy++) {
    for (let dx = 0; dx < 4; dx++) {
      const y = bumpY + dy;
      const x = bumpX + dx;
      if (y >= 0 && y < out.length && x >= 0 && x < out[y]!.length) {
        out[y]![x] = "2";
      }
    }
  }
  for (let dy = 0; dy < 4; dy++) {
    for (let dx = 0; dx < 4; dx++) {
      const y = holeY + dy;
      const x = holeX + dx;
      if (y >= 0 && y < out.length && x >= 0 && x < out[y]!.length) {
        out[y]![x] = ".";
      }
    }
  }
  return out.map((r) => r.join(""));
}

export function loadPortalFfaArtPack(): ArtPack {
  const sprites: Record<string, SpriteTemplate> = {};
  const anims: ArtPack["anims"] = {};
  for (const s of spriteList) {
    sprites[s.id] = s;
    const authored = COMBAT_ANIMS[s.id] ?? FX_ANIMS[s.id];
    if (authored) {
      const set = loadAuthoredDirectionalAnimSet(s.id, authored);
      anims[s.id] = set;
      const idle0 = authored.dirs.e.idle.frames[0];
      if (idle0) {
        const isFx = Boolean(FX_ANIMS[s.id]);
        sprites[s.id] = {
          id: s.id,
          size: s.size,
          rows: isFx ? idle0 : stampRoleSilhouetteTag(idle0, s.id),
        };
      }
    } else {
      anims[s.id] = singleFrameAnimSet(s, 2);
    }
  }
  return {
    manifest: packJson as ArtPack["manifest"],
    palette,
    affiliation,
    sprites,
    anims,
  };
}
